import React, { useEffect, useRef, useState } from 'react';
import { Col, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap';
import { Button, Input, Modal } from 'edozvola-frontend-lib/dist/ui/components';
import { FormikProvider, useFormik } from 'formik';
import { Label } from 'edozvola-frontend-lib/dist/ui';
import { useParams } from 'react-router-dom';
import { Loader } from 'app/shared/loader/loader';
import { fetchDkpLocationQuery, saveDkpLocationQuery } from 'app/edozvola/cases/case-location/service/case-location.queries';
import { initialDkpLocation } from 'app/edozvola/cases/case-location/model/case-location.model';
import { PaginatedDropdown } from 'edozvola-frontend-lib/dist/ui/components/paginated-dropdown';
import './case-location.scss';
import * as Yup from 'yup';
import {ERRORS, messages } from 'app/config/constants';
import axios from 'axios';
import { fetchCadastralMunicipalitiesQuery } from 'app/edozvola/codebooks/codebooks.queries';
import {showToast} from "app/shared/util/toast.util";
import {toast} from "react-toastify";

export interface IDkpLocationFormProps {
  dkpLocationId: string | null;
  setDkpLocationId: any;
  readOnly: boolean;
}

const CaseLocationDkpLocationForm = (props: IDkpLocationFormProps) => {
  const { id: uuid } = useParams<{ id: string }>();
  const parcelRegex = /^$|^\*$|^(\*?\d+\.?[a-zA-ZčČćĆžŽđĐ]*\*?)(\/\*?[a-zA-ZčČćĆžŽđĐ0-9]*\*?((?<=\d)(\.?[a-zA-ZčČćĆžŽđĐ]*))?)?$/;

  const { data: dkpLocationData, isLoading: dkpLocationDataLoading } = fetchDkpLocationQuery(props.dkpLocationId);

  const [validParcelValues, setValidParcelValues] = useState([]);

  const zisAbortRef = useRef<AbortController | null>(null);

  const zisSignal = () => {
    if (!zisAbortRef.current || zisAbortRef.current.signal.aborted) {
      zisAbortRef.current = new AbortController();
    }
    return zisAbortRef.current.signal;
  };

  const abortZisValidation = () => {
    zisAbortRef.current?.abort();
    zisAbortRef.current = null;
  };

  const caseDkpLocationValidationSchema = Yup.object({
    cadastralMunicipality: Yup.array().of(Yup.object()).min(1, messages.MANDATORY_FIELD),
    parcels: Yup.array()
      .of(
        Yup.object().shape({
          cadastralParcel: Yup.string()
            .required(messages.MANDATORY_FIELD)
            .test('unique', 'Unesene katastarske čestice unutar katastarske općine moraju biti različite!', (value, context) => {
              const { options } = context;
              const values = options.context?.parcels || [];
              const duplicates = values.filter(item => `${item.cadastralParcel}` === `${value}`);
              return duplicates.length <= 1;
            })
            .test('valid', 'Unesena katastarska čestica ne postoji u odabranoj općini!', (value, context) => {
              const { options } = context;
              const values = options.context?.cadastralMunicipality || [];
              const payload = {
                idCadastralMunicipality: values[0].id,
                cadastralParcel: value,
              };

              if (!validParcelValues.includes(value)) {
                return new Promise((resolve, reject) => {
                  axios
                    .post('api/zis/cadastral-parcel', payload, { signal: zisSignal() })
                    .then(() => {
                      resolve(true);
                      validParcelValues.push(value);
                    })
                    .catch(error => {
                      if (axios.isCancel(error)) {
                        resolve(true);
                        return;
                      }

                      const isZisError = ERRORS.ZIS_SERVICE_UNAVAILABLE_ERROR === error.response?.data?.detail;

                      if (isZisError) {
                        showToast(toast.error, ERRORS.ZIS_SERVICE_UNAVAILABLE_ERROR);
                        resolve(true);
                      } else {
                        resolve(false);
                      }
                    });
                });
              }

              return true;
            }),
        }),
      )
      .min(1, messages.MANDATORY_FIELD),
  });

  const formik = useFormik({
    initialValues: dkpLocationData || initialDkpLocation,
    onSubmit: values => saveDkpLocationMutation(values),
    validationSchema: caseDkpLocationValidationSchema,
    enableReinitialize: true,
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: true,
  });

  useEffect(() => {
    setValidParcelValues(formik.initialValues.parcels.map(parcel => parcel.cadastralParcel));
  }, [formik.initialValues]);

  useEffect(() => () => abortZisValidation(), []);

  const { data: cadastralMunicipalitiesOptions, isLoading: cadastralMunicipalityOptionsLoading } = fetchCadastralMunicipalitiesQuery();

  const { mutate: saveDkpLocationMutation, isPending: isDkpLocationSaving } = saveDkpLocationQuery(uuid, () => closeModal());

  const closeModal = () => {
    abortZisValidation();
    props.setDkpLocationId(null);
    formik.resetForm();
  };

  const onCadastralMunicipalityChange = (selected: any) => {
    formik
      .setValues({
        ...formik.values,
        parcels: [{ idDkpCadastralParcel: 0, cadastralParcel: '' }],
        cadastralMunicipality: selected,
      })
      .then(() => formik.validateForm());
  };

  const addNewParcel = () => {
    formik
      .setValues({
        ...formik.values,
        parcels: [...formik.values.parcels, { idDkpCadastralParcel: 0, cadastralParcel: '' }],
      })
      .then(() => formik.validateForm());
  };

  const deleteParcel = (indexToDelete: number) => {
    formik
      .setValues({
        ...formik.values,
        parcels: formik.values.parcels.filter((_, index) => index !== indexToDelete),
      })
      .then(() => formik.validateForm());
  };

  const parcelChanged = (event: any, field: string) => {
    if (event.target.value.match(parcelRegex) && event.target.value.length <= 10) {
      formik.setFieldValue(field, event.target.value.match(parcelRegex)[0]);
    }
    event.preventDefault();
  };

  return (
    <>
      <Modal scrollable size={'xl'} isOpen={props.dkpLocationId !== null}>
        {props.readOnly ? (
          <ModalHeader className={'pb-0'} toggle={() => closeModal()}>
            Pregled općine
          </ModalHeader>
        ) : (
          <ModalHeader className={'pb-0'}>{props.dkpLocationId === '' ? 'Unos općine' : 'Uređivanje općine'}</ModalHeader>
        )}
        <ModalBody>
          <FormikProvider value={formik}>
            {dkpLocationDataLoading && <Loader />}
            {!dkpLocationDataLoading && (
              <div className={'d-flex justify-content-between flex-column gap-3'}>
                <Row>
                  <Col>
                    <span className={'text-danger fw-bold'}>
                      U slučaju da nije moguće dohvatiti podatke iz sustava ZIS, molimo vas da pošaljete prijavu na email: helpdesk@apis-it.hr uz naznaku: ZIS servis nedostupan u eGrađevinskoj dozvoli.
                    </span>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Label labelText={'Katastarska općina'} />
                    <PaginatedDropdown
                      placeholder={!props?.readOnly && 'Odaberi katastarsku općinu'}
                      positionFixed
                      onItemSelect={onCadastralMunicipalityChange}
                      selected={formik.values?.cadastralMunicipality}
                      labelKey={'value'}
                      options={cadastralMunicipalityOptionsLoading ? [] : cadastralMunicipalitiesOptions}
                      clearButton
                      disabled={props.readOnly}
                      error={!props.readOnly && ((formik.errors?.cadastralMunicipality as string) ?? '')}
                      showErrorMessage
                    />
                  </Col>
                </Row>
                <div className={'d-flex justify-content-between flex-column gap-3'}>
                  {formik.values.parcels.map((parcel, index) => (
                    <div key={index}>
                      <Row>
                        <Col md={2}>
                          <Label labelText={'Katastarska čestica'} />
                          <Input
                            disabled={props.readOnly || formik.values?.cadastralMunicipality.length === 0}
                            value={parcel.cadastralParcel}
                            error={
                              !props.readOnly && formik.errors.parcels && formik.errors.parcels[index]
                                ? formik.errors.parcels[index]['cadastralParcel']
                                : ''
                            }
                            onChange={event => parcelChanged(event, `parcels.[${index}].cadastralParcel`)}
                            onBlur={() => formik.setFieldTouched(`parcels.[${index}].cadastralParcel`, true, true)}
                          />
                          {formik.isValidating && <Loader />}
                        </Col>
                        {!props.readOnly && formik.values.parcels.length > 1 && (
                          <Col md={2}>
                            <Button
                              className={'delete-parcel'}
                              color={'secondary'}
                              prependIcon={'trash'}
                              size={'lg'}
                              onClick={() => deleteParcel(index)}
                            />
                          </Col>
                        )}
                      </Row>
                    </div>
                  ))}
                </div>
                {!props.readOnly && (
                  <Row className={'mt-4'}>
                    <Col md={4}>
                      <Button disabled={!formik.isValid} label={'Dodaj česticu'} color={'secondary'} size={'lg'} onClick={addNewParcel} />
                    </Col>
                  </Row>
                )}
                <Row>
                  <Col md={6}>
                    <Label labelText={'ZK uložak'} />
                    <Input
                      disabled={
                        props.readOnly ||
                        formik.values?.cadastralMunicipality.length === 0 ||
                        formik.values?.parcels.length === 0 ||
                        !formik.isValid
                      }
                      value={formik.values?.zkEntry}
                      maxLength={255}
                      onChange={event => formik.setFieldValue(`zkEntry`, event.target.value)}
                    />
                  </Col>
                </Row>
              </div>
            )}
          </FormikProvider>
        </ModalBody>
        <ModalFooter>
          {!props.readOnly && (
            <>
              <Button label={'Odustani'} outline size={'lg'} onClick={closeModal} />
              <Button
                label={'Spremi'}
                size={'lg'}
                disabled={!formik.dirty || !formik.isValid || formik.isValidating}
                isLoading={isDkpLocationSaving}
                color={'info'}
                onClick={() => formik.handleSubmit()}
              />
            </>
          )}
        </ModalFooter>
      </Modal>
    </>
  );
};

export default CaseLocationDkpLocationForm;
